import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Film, Upload, Link as LinkIcon, Loader2, Youtube } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { slug:"", title:{fr:"",nl:"",en:""}, excerpt:{fr:"",nl:"",en:""}, content:{fr:"",nl:"",en:""}, cover_image:"", tags:[], status:"draft" };

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [tagsText, setTagsText] = useState("");
  const [videoDialog, setVideoDialog] = useState({ open: false, lang: "fr" });
  const [videoUrl, setVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const textareaRefs = useRef({});

  const load = () => api.get("/blog", { params: { all: true } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setTagsText(""); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, title: {fr:"",nl:"",en:"", ...p.title}, excerpt: {fr:"",nl:"",en:"", ...p.excerpt}, content: {fr:"",nl:"",en:"", ...p.content} });
    setTagsText((p.tags || []).join(", "));
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, tags: tagsText.split(",").map(s => s.trim()).filter(Boolean) };
    if (!payload.slug) { toast.error("Slug requis"); return; }
    try {
      if (form.id) await api.put(`/blog/${form.id}`, payload);
      else await api.post("/blog", payload);
      toast.success(form.id ? "Article mis à jour" : "Article créé");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/blog/${id}`); toast.success("Supprimé"); load(); } };

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const setL = (k, lang, v) => setForm(s => ({ ...s, [k]: { ...s[k], [lang]: v } }));

  // Insertion d'une vidéo dans le contenu à la position du curseur
  const insertVideoTag = (lang, url) => {
    const ta = textareaRefs.current[lang];
    const current = form.content[lang] || "";
    const insertion = `\n\n[video]${url}[/video]\n\n`;
    let newValue;
    if (ta) {
      const start = ta.selectionStart ?? current.length;
      const end = ta.selectionEnd ?? current.length;
      newValue = current.slice(0, start) + insertion + current.slice(end);
      setTimeout(() => {
        ta.focus();
        const pos = start + insertion.length;
        ta.setSelectionRange(pos, pos);
      }, 30);
    } else {
      newValue = current + insertion;
    }
    setL("content", lang, newValue);
  };

  const openVideoDialog = (lang) => {
    setVideoDialog({ open: true, lang });
    setVideoUrl("");
  };

  const confirmVideoUrl = () => {
    const trimmed = videoUrl.trim();
    if (!trimmed) { toast.error("URL requise"); return; }
    insertVideoTag(videoDialog.lang, trimmed);
    setVideoDialog({ open: false, lang: "fr" });
    toast.success("Vidéo insérée dans l'article");
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Fichier trop lourd (max 200 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertVideoTag(videoDialog.lang, r.data.url);
      setVideoDialog({ open: false, lang: "fr" });
      toast.success("Vidéo téléversée et insérée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl tracking-tight font-light text-white">Blog</h1>
        <Button data-testid="new-post" onClick={openNew} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">
          <Plus className="w-4 h-4 mr-2" /> Nouveau
        </Button>
      </div>

      <div className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-white/50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-4">Titre</th>
              <th className="text-left px-6 py-4">Slug</th>
              <th className="text-left px-6 py-4">Statut</th>
              <th className="text-right px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-t border-white/5">
                <td className="px-6 py-4 text-white">{p.title?.fr || p.title?.en || "—"}</td>
                <td className="px-6 py-4 text-white/60 text-xs font-mono">{p.slug}</td>
                <td className="px-6 py-4">
                  <span className={`text-[10px] uppercase tracking-widest ${p.status === "published" ? "text-emerald-400" : "text-amber-400"}`}>
                    {p.status === "published" ? "Publié" : "Brouillon"}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#C8A75A] p-2 inline-block"><ExternalLink className="w-4 h-4" /></a>}
                  <button onClick={() => openEdit(p)} className="text-white/60 hover:text-[#C8A75A] p-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-white/40">Aucun article</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Modifier" : "Nouvel article"}</DialogTitle></DialogHeader>

          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <F label="Slug (URL, sans espaces)"><Input value={form.slug} onChange={e => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className="bg-[#0D0D0D] border-white/10 font-mono" /></F>
            <F label="Image de couverture (URL)"><Input value={form.cover_image} onChange={e => set("cover_image", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
          </div>

          {["fr","nl","en"].map(l => (
            <div key={l} className="mt-4 border-t border-white/5 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{l.toUpperCase()}</p>
                <button
                  type="button"
                  data-testid={`insert-video-${l}`}
                  onClick={() => openVideoDialog(l)}
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest bg-[#C8A75A]/10 border border-[#C8A75A]/40 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black transition-colors px-3 py-2 rounded-md"
                >
                  <Film className="w-3.5 h-3.5" />
                  Insérer une vidéo
                </button>
              </div>
              <F label={`Titre (${l})`}><Input value={form.title[l]} onChange={e => setL("title", l, e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
              <F label={`Résumé (${l})`}><Textarea rows={2} value={form.excerpt[l]} onChange={e => setL("excerpt", l, e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
              <F label={`Contenu (${l}) — séparez les paragraphes par une ligne vide. Utilisez le bouton "Insérer une vidéo" pour ajouter YouTube/Vimeo/MP4.`}>
                <Textarea
                  ref={(el) => { textareaRefs.current[l] = el; }}
                  rows={10}
                  value={form.content[l]}
                  onChange={e => setL("content", l, e.target.value)}
                  className="bg-[#0D0D0D] border-white/10 font-mono text-sm"
                  placeholder="Votre texte…&#10;&#10;[video]https://youtu.be/xyz[/video]&#10;&#10;Suite du texte…"
                />
              </F>
            </div>
          ))}

          <div className="grid md:grid-cols-2 gap-3 mt-4 border-t border-white/5 pt-4">
            <F label="Tags (séparés par virgules)"><Input value={tagsText} onChange={e => setTagsText(e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="Porsche, arrivage, simulateur" /></F>
            <F label="Statut">
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/20 text-white hover:bg-white/5">Annuler</Button>
            <Button onClick={save} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video insertion dialog */}
      <Dialog open={videoDialog.open} onOpenChange={(o) => setVideoDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Insérer une vidéo · {videoDialog.lang?.toUpperCase()}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2 mb-2">
                <LinkIcon className="w-3 h-3" /> Coller une URL YouTube ou Vimeo
              </label>
              <div className="flex gap-2">
                <Input
                  data-testid="video-url-input"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtu.be/xxxxxxx ou https://vimeo.com/xxxxxxx"
                  className="bg-[#0D0D0D] border-white/10"
                />
                <Button
                  data-testid="video-url-insert"
                  onClick={confirmVideoUrl}
                  className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest"
                >
                  Insérer
                </Button>
              </div>
              <p className="text-white/40 text-xs mt-2 flex items-center gap-2">
                <Youtube className="w-3 h-3 text-red-400" /> Formats acceptés : YouTube, Vimeo, ou MP4 direct
              </p>
            </div>
            <div className="border-t border-white/10 pt-4">
              <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2 mb-2">
                <Upload className="w-3 h-3" /> Ou téléverser un fichier MP4
              </label>
              <label
                data-testid="video-upload-label"
                className={`inline-flex items-center gap-2 bg-black/40 border border-white/10 hover:border-[#C8A75A]/50 rounded-md px-4 py-3 cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-[#C8A75A]" />}
                <span className="text-sm">{uploading ? "Envoi…" : "Choisir un fichier vidéo (MP4, max 200 Mo)"}</span>
                <input
                  data-testid="video-upload-input"
                  type="file"
                  accept="video/mp4,video/webm,video/*"
                  onChange={uploadVideo}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="mt-3"><label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>{children}</div>;
}
